// Ítem de Tiendanube en Mi marca (estilo nav, debajo de "Mis Modelos"):
//   • Si la marca NO tiene tienda conectada → botón "Conectar" + modal explicativo
//     (componente compartido `TiendaNubeConnectModal`, con explicación).
//   • Si YA está conectada → "conectada a [tienda] · Desconectar" (modal de confirmación).
// El estado lo trae `getTnStatus` (liviano). Connect/disconnect via `lib/tiendaNube`.

import React from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { getTnStatus, disconnectTn, connectTn } from "../lib/tiendaNube";
import { TiendaNubeIcon } from "./TiendaNubeIcon";
import { TiendaNubeConnectModal } from "./TiendaNubeConnectModal";

export const TiendaNubeStatus: React.FC<{
  companyId: string | null;
  brandId: string | null;
}> = ({ companyId, brandId }) => {
  const [status, setStatus] = React.useState<{
    connected: boolean;
    storeName: string | null;
    companyHasStores: boolean;
  } | null>(null);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [showConnect, setShowConnect] = React.useState(false);
  const [connecting, setConnecting] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!companyId) {
      setStatus(null);
      return;
    }
    (async () => {
      const s = await getTnStatus(brandId);
      if (!cancelled) setStatus(s);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, brandId]);

  if (!status) return null; // cargando / sin empresa

  const confirmDisconnect = async () => {
    if (disconnecting) return;
    setDisconnecting(true);
    try {
      await disconnectTn(brandId);
      setStatus(await getTnStatus(brandId));
      setShowConfirm(false);
    } catch {
      /* si falla, queda como estaba */
    } finally {
      setDisconnecting(false);
    }
  };

  const handleConnect = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      await connectTn(brandId);
      setStatus(await getTnStatus(brandId)); // refrescar → queda conectada
      setShowConnect(false);
    } catch {
      /* cancelado / popup bloqueado → queda como estaba */
    } finally {
      setConnecting(false);
    }
  };

  return (
    <>
      {status.connected ? (
        // ── Conectada: nombre de tienda + Desconectar ──
        <div className="mt-2 w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-black/[0.07] bg-white">
          <div className="flex items-center gap-2.5 min-w-0">
            <TiendaNubeIcon className="h-[18px] w-auto shrink-0 text-[#2c3357]" />
            <div className="min-w-0">
              <div className="text-[14px] font-medium text-brand-black/80 leading-tight">
                Tiendanube
              </div>
              <div className="text-[12px] text-brand-black/45 truncate leading-tight">
                {status.storeName || "Conectada"}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            className="shrink-0 inline-flex items-center gap-1 text-[12.5px] font-medium text-neutral-500 hover:text-neutral-700 transition-colors"
          >
            Desconectar
          </button>
        </div>
      ) : (
        // ── No conectada: botón Conectar ──
        <div className="mt-2 w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-black/[0.07] bg-white">
          <div className="flex items-center gap-2.5 min-w-0">
            <TiendaNubeIcon className="h-[18px] w-auto shrink-0 text-[#2c3357]" />
            <div className="text-[14px] font-medium text-brand-black/80 leading-tight">
              Tiendanube
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowConnect(true)}
            className="shrink-0 inline-flex items-center gap-1 text-[12.5px] font-medium text-brand-primary hover:opacity-70 transition-colors"
          >
            Conectar
          </button>
        </div>
      )}

      {/* Modal Conectar (con explicación) — componente compartido con el import */}
      <TiendaNubeConnectModal
        open={showConnect}
        companyHasStores={status.companyHasStores}
        connecting={connecting}
        onCancel={() => setShowConnect(false)}
        onConnect={handleConnect}
      />

      {/* Modal: confirmar Desconexión */}
      {showConfirm &&
        createPortal(
          <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[80]">
            <div className="bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-sm w-full p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">
                  Desconectar Tiendanube
                </h3>
                <button
                  type="button"
                  onClick={() => !disconnecting && setShowConfirm(false)}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-6">
                <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
                  <TiendaNubeIcon className="h-5 w-auto text-[#2c3357]" />
                </div>
                <p className="text-[length:var(--text-body)] text-neutral-600 text-center">
                  ¿Desconectar tu Tiendanube de{" "}
                  <span className="font-medium text-neutral-800">
                    {status.storeName || "esta tienda"}
                  </span>{" "}
                  de esta marca? Vas a poder volver a conectarla cuando quieras.
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowConfirm(false)}
                  disabled={disconnecting}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmDisconnect}
                  disabled={disconnecting}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-1.5 rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors bg-neutral-900 hover:bg-neutral-800 text-white disabled:opacity-60"
                >
                  {disconnecting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {disconnecting ? "Desconectando…" : "Desconectar"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default TiendaNubeStatus;
